import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelInvitationQuery, getBoardInvitationsQuery, inviteMember, respondToInvitationQuery } from '../services/invitationService';
import { BoardInvitation, MemberRole } from '../contract';
import { withoutBoardInvitation } from './invitationTransforms';
import { NotificationList, withoutInvitation } from './notificationTransforms';
import { queryKeys } from './queryKeys';

// Joining a board. The owner invites and can see (and cancel) the invitations not yet answered; the person invited sees
// them in the bell (data/notifications.ts) and accepts or declines.

// --- the owner's side -----------------------------------------------------------------------------------------------

export const boardInvitationsOptions = (boardId: string) => ({
    queryKey: queryKeys.boardInvitations(boardId),
    queryFn: () => getBoardInvitationsQuery(boardId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
});

// `enabled: false` waits (only the owner may see them).
export const useBoardInvitations = (boardId: string, options: { enabled?: boolean } = {}) =>
    useQuery({ ...boardInvitationsOptions(boardId), ...(options.enabled === undefined ? {} : { enabled: options.enabled }) });

export type NewInvitation = { email: string, role: MemberRole, kiosk: boolean };

export const inviteMemberMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ email, role, kiosk }: NewInvitation) => inviteMember(boardId, email, role, kiosk),
    // The server fills in who was invited and when, so read the list back rather than guess it.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.boardInvitations(boardId) }),
});

export const cancelInvitationMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (invitationId: string) => cancelInvitationQuery(invitationId),
    onSuccess: (_result: unknown, invitationId: string) => {
        queryClient.setQueryData<BoardInvitation[]>(queryKeys.boardInvitations(boardId), (list) => withoutBoardInvitation(list, invitationId));
    },
});

// --- the person invited ---------------------------------------------------------------------------------------------

export type Answer = { invitationId: string, accept: boolean };

export const respondToInvitationMutation = (queryClient: QueryClient) => ({
    mutationFn: ({ invitationId, accept }: Answer) => respondToInvitationQuery(invitationId, accept),
    onSuccess: (_result: unknown, { invitationId, accept }: Answer) => {
        queryClient.setQueryData<NotificationList>(queryKeys.notifications(), (list) => withoutInvitation(list, invitationId));
        // Joining puts a board on the person's list.
        if (accept) queryClient.invalidateQueries({ queryKey: queryKeys.boards() });
    },
    // Most likely it was cancelled (or expired) before the answer arrived: read the list again so it disappears.
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications() }),
});

export const useInviteMember = (boardId: string) => useMutation(inviteMemberMutation(useQueryClient(), boardId));
export const useCancelInvitation = (boardId: string) => useMutation(cancelInvitationMutation(useQueryClient(), boardId));
export const useRespondToInvitation = () => useMutation(respondToInvitationMutation(useQueryClient()));
